import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { classifierLabel } from '../../../services/classifiersApi';
import {
  fetchProblemEvalSuggestion,
  fetchProblemEvalSuggestions,
} from '../../../services/problemRcaApi';
import { formatCount, formatDate } from '../../../utils/formatters';
import { components } from '../../../utils/designTokens';
import { shortId, statusStyle } from './rcaHelpers';
import { DetailList, FixList, KeyValueGrid, Section, TextList } from './RcaPrimitives';
import FeedbackPanel from './FeedbackPanel';
import RelatedTraceRow from './RelatedTraceRow';
import CodeSnippet from '../../../components/CodeSnippet';

function formatLabel(value) {
  return String(value || '')
    .replace(/[._-]+/g, ' ')
    .replace(/\b\w/g, char => char.toUpperCase());
}

function stringifyJson(value) {
  if (!value || (typeof value === 'object' && Object.keys(value).length === 0)) return '';
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function EvalSuggestionCard({ suggestion }) {
  const assertions = Array.isArray(suggestion?.eval_spec?.assertions) ? suggestion.eval_spec.assertions : [];
  const limitations = Array.isArray(suggestion?.limitations) ? suggestion.limitations : [];
  const targetFrameworks = Array.isArray(suggestion?.target_frameworks) ? suggestion.target_frameworks : [];
  const traceSet = suggestion?.trace_set || {};
  const primaryFailure = Array.isArray(traceSet.primary_failure) ? traceSet.primary_failure : [];
  const implementationPrompt = suggestion?.implementation_prompt || '';
  const evalSpecJson = stringifyJson(suggestion?.eval_spec);

  return (
    <div className="rounded border border-border-subtle bg-background-primary p-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-text-primary">
            {suggestion?.title || formatLabel(suggestion?.eval_type) || 'Eval suggestion'}
          </div>
          <div className="mt-1 text-xs text-text-muted">
            {formatLabel(suggestion?.eval_type)} · {formatLabel(suggestion?.readiness)} readiness
            {targetFrameworks.length > 0 ? ` · ${targetFrameworks.join(', ')}` : ''}
          </div>
        </div>
        <span className="rounded border border-border-subtle px-2 py-1 text-[11px] uppercase tracking-wide text-text-muted">
          {suggestion?.status || 'active'}
        </span>
      </div>

      {suggestion?.summary && (
        <p className="mt-3 text-sm leading-relaxed text-text-primary">{suggestion.summary}</p>
      )}

      {assertions.length > 0 && (
        <div className="mt-3">
          <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-text-muted">Assertions</div>
          <div className="space-y-2">
            {assertions.map((assertion, idx) => (
              <div key={`${assertion?.type || 'assertion'}:${idx}`} className="rounded border border-border-subtle bg-background-secondary px-3 py-2">
                <div className="font-mono text-xs text-accent-primary-base">{assertion?.type || `assertion_${idx + 1}`}</div>
                <div className="mt-1 text-xs leading-relaxed text-text-primary">{assertion?.description || 'No assertion description.'}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {primaryFailure.length > 0 && (
        <div className="mt-3">
          <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-text-muted">Primary Failure Traces</div>
          <div className="space-y-1">
            {primaryFailure.map((item, idx) => (
              <div key={`${item?.trace_id || 'trace'}:${idx}`} className="text-xs text-text-muted">
                <span className="font-mono text-accent-primary-base">{shortId(item?.trace_id)}</span>
                {item?.reason ? <span> · {item.reason}</span> : null}
              </div>
            ))}
          </div>
        </div>
      )}

      {limitations.length > 0 && (
        <div className="mt-3 text-xs text-text-muted">
          Limitations: {limitations.map(formatLabel).join(', ')}
        </div>
      )}

      {(implementationPrompt || evalSpecJson) && (
        <div className="mt-3 space-y-2">
          {implementationPrompt && (
            <details className="rounded border border-border-subtle bg-background-secondary">
              <summary className="cursor-pointer px-3 py-2 text-xs font-semibold text-text-primary">
                Coding-agent prompt
              </summary>
              <div className="border-t border-border-subtle p-3">
                <CodeSnippet code={implementationPrompt} language="text" />
              </div>
            </details>
          )}
          {evalSpecJson && (
            <details className="rounded border border-border-subtle bg-background-secondary">
              <summary className="cursor-pointer px-3 py-2 text-xs font-semibold text-text-primary">
                Eval spec JSON
              </summary>
              <div className="border-t border-border-subtle p-3">
                <CodeSnippet code={evalSpecJson} language="json" />
              </div>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

function EvalSuggestionsSection({ suggestions, loading, error }) {
  if (loading) {
    return (
      <Section title="Suggested Evals">
        <div className="text-sm text-text-muted">Loading eval suggestions...</div>
      </Section>
    );
  }

  if (error) {
    return (
      <Section title="Suggested Evals">
        <div className="text-sm text-status-danger">{error}</div>
      </Section>
    );
  }

  const rows = Array.isArray(suggestions) ? suggestions : [];
  if (rows.length === 0) return null;

  return (
    <Section title="Suggested Evals">
      <div className="space-y-3">
        {rows.map((suggestion) => (
          <EvalSuggestionCard
            key={suggestion.eval_suggestion_id || `${suggestion.eval_type}:${suggestion.source_subtype}`}
            suggestion={suggestion}
          />
        ))}
      </div>
    </Section>
  );
}

export default function RcaDetail({
  rca, appId, environment, selectedRange, jwt, actionBusy, onFeedback, onThumbsDown, onResolution,
  inlineTracePreview = false, linkTarget = 'navigate', showReview = true, showFeedback = true, showResolution = true,
  evidenceChart = null,
}) {
  const navigate = useNavigate();
  const [evalSuggestions, setEvalSuggestions] = useState([]);
  const [evalSuggestionsLoading, setEvalSuggestionsLoading] = useState(false);
  const [evalSuggestionsError, setEvalSuggestionsError] = useState('');
  const instances = Array.isArray(rca?.instances) ? rca.instances : [];
  const events = Array.isArray(rca?.events) ? rca.events : [];
  const exampleTraceId = rca?.example?.trace_id || instances[0]?.trace_id || '';
  const persisted = !rca?.__preview;
  const problemNative = Boolean(rca?.__problemNative);
  const problemId = rca?.problem_id || '';

  useEffect(() => {
    setEvalSuggestions([]);
    setEvalSuggestionsError('');
    if (!persisted || !problemId || !appId || !environment || !jwt) {
      setEvalSuggestionsLoading(false);
      return undefined;
    }

    const abortController = new AbortController();
    setEvalSuggestionsLoading(true);
    (async () => {
      try {
        const list = await fetchProblemEvalSuggestions(appId, environment, selectedRange, problemId, jwt, {
          signal: abortController.signal,
          limit: 20,
        });
        const compact = Array.isArray(list.suggestions) ? list.suggestions : [];
        const details = await Promise.all(compact.map(async (item) => {
          const id = item?.eval_suggestion_id || '';
          if (!id) return item;
          try {
            return await fetchProblemEvalSuggestion(appId, environment, selectedRange, problemId, id, jwt, {
              signal: abortController.signal,
            });
          } catch {
            return item;
          }
        }));
        if (!abortController.signal.aborted) setEvalSuggestions(details.filter(Boolean));
      } catch (err) {
        if (!abortController.signal.aborted && err?.name !== 'AbortError') {
          setEvalSuggestionsError(err?.message || 'Failed to load eval suggestions');
        }
      } finally {
        if (!abortController.signal.aborted) setEvalSuggestionsLoading(false);
      }
    })();

    return () => abortController.abort();
  }, [appId, environment, jwt, persisted, problemId, selectedRange]);

  const openUrl = useCallback((url) => {
    if (linkTarget === 'newTab') {
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      navigate(url);
    }
  }, [linkTarget, navigate]);

  const openTrace = useCallback((traceId, env) => {
    if (!traceId) return;
    const params = new URLSearchParams();
    if (env || environment) params.set('env', env || environment);
    if (selectedRange) params.set('range', selectedRange);
    openUrl(`/app/${appId}/traces/${encodeURIComponent(traceId)}?${params.toString()}`);
  }, [appId, environment, selectedRange, openUrl]);

  const openRcaTraces = useCallback(() => {
    const params = new URLSearchParams();
    if (environment) params.set('env', environment);
    if (selectedRange) params.set('range', selectedRange);

    const problemId = rca?.problem_id || rca?.problem?.problem_id || '';
    if (!problemId) {
      return;
    }
    params.set('problem_id', problemId);
    if (rca?.classifier_key) {
      params.set('classifier', rca.classifier_key);
      params.set('classifier_status', 'detected');
    }
    params.set('from', 'rca');
    openUrl(`/app/${appId}/traces?${params.toString()}`);
  }, [appId, rca, environment, selectedRange, openUrl]);

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-border-subtle bg-background-secondary p-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-lg font-semibold text-text-primary">{rca.title || 'RCA'}</h2>
              <span
                className={`border uppercase ${components.badge.padding} ${components.badge.rounded} ${components.badge.size} ${components.badge.weight}`}
                style={statusStyle(rca.status)}
              >
                {rca.status || 'open'}
              </span>
            </div>
            <div className="mt-1 text-xs text-text-muted">
              {classifierLabel(rca.classifier_key)} · v{rca.classifier_version} · RCA {shortId(rca.rca_id)}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {!problemNative && (
              <button
                type="button"
                className="rounded border border-border-subtle px-3 py-1.5 text-xs text-text-primary hover:bg-background-tertiary"
                onClick={openRcaTraces}
              >
                View RCA traces
              </button>
            )}
            {exampleTraceId && (
            <button
              type="button"
              className="rounded border border-border-subtle px-3 py-1.5 text-xs text-text-primary hover:bg-background-tertiary"
              onClick={() => openTrace(exampleTraceId, rca.example?.environment)}
            >
              Open example trace
            </button>
            )}
          </div>
        </div>
        <div className="mt-4 grid gap-3 sm:grid-cols-5">
          <div>
            <div className="text-[11px] uppercase tracking-wide text-text-muted">Traces</div>
            <div className="text-sm font-semibold text-text-primary">{formatCount(rca.trace_count)}</div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wide text-text-muted">Services</div>
            <div className="text-sm font-semibold text-text-primary">{formatCount(rca.service_count)}</div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wide text-text-muted">First seen</div>
            <div className="text-sm text-text-primary">{formatDate(rca.first_seen)}</div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wide text-text-muted">Last seen</div>
            <div className="text-sm text-text-primary">{formatDate(rca.last_seen)}</div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wide text-text-muted">Confidence</div>
            <div className="text-sm font-semibold capitalize text-text-primary">{rca.confidence || '—'}</div>
          </div>
        </div>
      </div>

      {persisted && showReview && (
        <FeedbackPanel
          rca={rca}
          busy={actionBusy}
          onFeedback={onFeedback}
          onThumbsDown={onThumbsDown}
          onResolution={onResolution}
          showFeedback={showFeedback}
          showResolution={showResolution}
        />
      )}

      <Section title="Summary">
        <p className="text-sm leading-relaxed text-text-primary">{rca.summary || 'No summary available.'}</p>
      </Section>

      <Section title="Likely Root Cause">
        <p className="text-sm leading-relaxed text-text-primary">{rca.root_cause || 'No root cause available.'}</p>
      </Section>

      <Section title="Why It Happened">
        <TextList items={rca.why_it_happened} />
      </Section>

      {rca.impact && (
        <Section title="Impact">
          <p className="text-sm leading-relaxed text-text-primary">{rca.impact}</p>
        </Section>
      )}

      {(evidenceChart || (Array.isArray(rca.evidence) && rca.evidence.length > 0)) && (
        <Section title="Evidence">
          {evidenceChart && <div className="mb-3">{evidenceChart}</div>}
          {Array.isArray(rca.evidence) && rca.evidence.length > 0 && <DetailList items={rca.evidence} />}
        </Section>
      )}

      {rca.evidence_pattern && (
        <Section title="Evidence Pattern">
          <KeyValueGrid value={rca.evidence_pattern} />
        </Section>
      )}

      {Array.isArray(rca.timeline) && rca.timeline.length > 0 && (
        <Section title="Timeline">
          <DetailList items={rca.timeline} />
        </Section>
      )}

      <Section title="Recommended Fixes">
        <FixList fixes={rca.recommended_fixes} />
      </Section>

      <EvalSuggestionsSection
        suggestions={evalSuggestions}
        loading={evalSuggestionsLoading}
        error={evalSuggestionsError}
      />

      {Array.isArray(rca.limitations) && rca.limitations.length > 0 && (
        <Section title="Limitations">
          <TextList items={rca.limitations} />
        </Section>
      )}

      <Section title="Related Traces">
        {instances.length === 0 ? (
          <div className="text-sm text-text-muted">No related traces visible in this service scope.</div>
        ) : inlineTracePreview ? (
          <div className="space-y-2">
            {instances.slice(0, 20).map((instance) => (
              <RelatedTraceRow
                key={`${instance.service_id}:${instance.environment}:${instance.trace_id}`}
                instance={instance}
                appId={appId}
                environment={environment}
                selectedRange={selectedRange}
                jwt={jwt}
              />
            ))}
          </div>
        ) : (
          <div className="space-y-2">
            {instances.slice(0, 20).map((instance) => (
              <button
                key={`${instance.service_id}:${instance.environment}:${instance.trace_id}`}
                type="button"
                className="block w-full rounded border border-border-subtle bg-background-primary px-3 py-2 text-left hover:bg-background-tertiary"
                onClick={() => openTrace(instance.trace_id, instance.environment)}
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-mono text-xs text-accent-primary-base">{shortId(instance.trace_id)}</span>
                  <span className="text-[11px] text-text-muted">{formatDate(instance.updated_at)}</span>
                </div>
                <div className="mt-1 text-xs text-text-muted">
                  {instance.service_name || instance.service_id} · {instance.environment || 'environment unknown'} · {formatCount(instance.evidence_span_ids?.length || 0)} evidence spans
                </div>
              </button>
            ))}
          </div>
        )}
      </Section>

      <Section title="Activity">
        {events.length === 0 ? (
          <div className="text-sm text-text-muted">No feedback or resolution events yet.</div>
        ) : (
          <div className="space-y-2">
            {events.map((event) => (
              <div key={event.event_id} className="rounded border border-border-subtle bg-background-primary px-3 py-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-xs font-semibold text-text-primary">{String(event.event_type || '').replace(/_/g, ' ')}</span>
                  <span className="text-[11px] text-text-muted">{formatDate(event.created_at)}</span>
                </div>
                {event.note && <div className="mt-1 text-xs text-text-muted">{event.note}</div>}
              </div>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}
